import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faPlus, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ConsultationService, sharedApiClient } from '../fetcher';
import { useNotifications } from '@medprompt/shared';
import NotesTemplateEditorModal from './NotesTemplateEditorModal';
import '@medprompt/shared/src/common/modals.css';
import '../detail/tabs/TemplateManager.css';

const NotesTemplateManagerModal = ({ onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const { addNotification } = useNotifications();
  
  const consultationService = useMemo(() => new ConsultationService(sharedApiClient), []);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTemplates = await consultationService.fetchNotesTemplates();
      setTemplates(fetchedTemplates || []);
    } catch (err) {
      console.error('Error fetching notes templates:', err);
      setError('Failed to load notes templates');
      addNotification('notes-template-manager', 'Failed to load notes templates', { isError: true });
    } finally {
      setLoading(false);
    }
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setShowEditorModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowEditorModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this notes template? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTemplateId(templateId);
      await consultationService.deleteNotesTemplate(templateId);
      
      addNotification('notes-template-manager', 'Notes template deleted successfully', { isError: false });
      
      await fetchTemplates();
    } catch (err) {
      console.error('Error deleting notes template:', err);
      addNotification('notes-template-manager', 'Failed to delete notes template', { isError: true });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleTemplateSave = async (templateData) => {
    try {
      if (editingTemplate) {
        const updateData = {
          templateId: editingTemplate.templateId,
          title: templateData.title,
          content: templateData.content
        };
        await consultationService.updateNotesTemplate(updateData);
        
        addNotification('notes-template-manager', 'Notes template updated successfully', { isError: false });
      } else {
        const createData = {
          templateId: null,
          title: templateData.title,
          content: templateData.content
        };
        await consultationService.createNotesTemplate(createData);
        
        addNotification('notes-template-manager', 'Notes template created successfully', { isError: false });
      }
      
      setShowEditorModal(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Error saving notes template:', err);
      addNotification('notes-template-manager', `Failed to ${editingTemplate ? 'update' : 'create'} notes template`, { isError: true });
      throw err;
    }
  };

  const handleCloseEditor = () => {
    setShowEditorModal(false);
    setEditingTemplate(null);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container template-manager-modal modal-animated" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header template-manager-header">
            <h2>Manage Notes Templates</h2>
            <button className="close-button" onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="modal-content template-manager-content">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#50b97d' }} />
                <p style={{ marginTop: '15px', color: '#666' }}>Loading notes templates...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: '#dc3545', marginBottom: '15px' }}>{error}</p>
                <button className="button" onClick={fetchTemplates}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                <div className="template-list">
                  {templates.length === 0 ? (
                    <div className="empty-templates-message">
                      No notes templates created yet. Click "Add Template" to create one.
                    </div>
                  ) : (
                    templates.map((template) => (
                      <div key={template.templateId} className="template-item">
                        <div className="template-info">
                          <div className="template-title">{template.title}</div>
                          <div className="template-content-preview">
                            {template.content.length > 150 
                              ? `${template.content.substring(0, 150)}...` 
                              : template.content}
                          </div>
                        </div>
                        <div className="template-actions">
                          <button
                            className="other-button edit-button"
                            onClick={() => handleEditTemplate(template)}
                            disabled={deletingTemplateId === template.templateId}
                            title="Edit template"
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </button>
                          <button
                            className="other-button delete-template-button"
                            onClick={() => handleDeleteTemplate(template.templateId)}
                            disabled={deletingTemplateId === template.templateId}
                            title="Delete template"
                          >
                            {deletingTemplateId === template.templateId ? (
                              <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} />
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <button
                  className="add-template-button"
                  onClick={handleAddTemplate}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faPlus} className="add-icon" />
                  Add Template
                </button>
              </>
            )}
          </div>

          <div className="modal-actions">
            <button className="cancel-button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {showEditorModal && (
        <NotesTemplateEditorModal
          template={editingTemplate}
          onSave={handleTemplateSave}
          onClose={handleCloseEditor}
        />
      )}
    </>
  );
};

export default React.memo(NotesTemplateManagerModal);
